import React from 'react';
import { motion } from 'framer-motion';
import {
  Shield, Lock, Clock, TrendingUp, Users, BarChart3,
  CheckCircle, ArrowRight, Sparkles,
  Bolt, Target, Gauge, Award, Layers, Activity,
  Send, GitBranch, CalendarCheck
} from 'lucide-react';
import '../styles/Features.css';
import { FeaturesHero } from '../components/PageHeroes';

const Features = () => {
  const mainFeatures = [
    {
      icon: Shield,
      title: 'HMRC & VAT Compliant',
      description: 'Fully compliant with UK PAYE regulations, tax requirements, and VAT filing'
    },
    {
      icon: Lock,
      title: 'Bank-Level Security',
      description: 'Enterprise-grade encryption for all your sensitive payroll and financial data'
    },
    {
      icon: Clock,
      title: 'Real-Time Processing',
      description: 'Process payroll, invoices, and journals instantly with automatic calculations and updates'
    },
    {
      icon: TrendingUp,
      title: 'Advanced Analytics',
      description: 'Detailed insights and reporting on payroll, cash flow, and business costs'
    },
    {
      icon: Users,
      title: 'Multi-User Access',
      description: 'Manage team access with granular permission controls and approval workflows'
    },
    {
      icon: BarChart3,
      title: 'Custom Reports',
      description: 'Generate comprehensive financial and operational reports tailored to your needs'
    }
  ];

  const featureCategories = [
    {
      icon: Bolt,
      title: 'Automated Payroll',
      color: '#ec4899',
      colorLight: '#fce7f3',
      features: [
        'Automatic tax calculations',
        'NI contribution handling',
        'Pension deductions',
        'Custom allowances & deductions',
        'Batch processing',
        'Scheduled payroll runs'
      ]
    },
    {
      icon: Activity,
      title: 'Employee Management',
      color: '#8b5cf6',
      colorLight: '#f3e8ff',
      features: [
        'Employee database',
        'Leave management',
        'Recruitment & onboarding',
        'Digital payslips',
        'Employee self-service',
        'Document storage'
      ]
    },
    {
      icon: Send,
      title: 'Invoicing & Accounts',
      color: '#06b6d4',
      colorLight: '#cffafe',
      features: [
        'Customer invoicing & quotes',
        'Supplier bills & expense claims',
        'Bank reconciliation',
        'General ledger & journals',
        'VAT returns',
        'Live financial reporting'
      ]
    },
    {
      icon: GitBranch,
      title: 'Workflow & Operations',
      color: '#10b981',
      colorLight: '#dcfce7',
      features: [
        'Custom approval workflows',
        'Appointment scheduling',
        'Document sign-off & version history',
        'Email marketing campaigns',
        'API access',
        'Accounting software integration'
      ]
    },
    {
      icon: Shield,
      title: 'Compliance & Security',
      color: '#ec4899',
      colorLight: '#fce7f3',
      features: [
        'GDPR compliant',
        'ISO 27001 certified',
        'Daily backups',
        'Audit trails',
        'Role-based access',
        'Data encryption'
      ]
    }
  ];

  const benefits = [
    {
      icon: Sparkles,
      title: 'Save Time',
      description: 'Automate manual payroll and admin tasks and free up your team for strategic work',
      stat: '10 hrs/week'
    },
    {
      icon: Target,
      title: 'Reduce Errors',
      description: 'Eliminate manual calculation mistakes with intelligent automation',
      stat: '99.9% Accurate'
    },
    {
      icon: Award,
      title: 'Ensure Compliance',
      description: 'Always stay compliant with the latest UK tax, VAT, and employment laws',
      stat: '100% HMRC'
    },
    {
      icon: Gauge,
      title: 'Control Costs',
      description: 'Get complete visibility into payroll and business costs and budget effectively',
      stat: 'Save 30%'
    },
    {
      icon: Users,
      title: 'Employee Experience',
      description: 'Give employees self-service access to payroll, leave, and expense claims',
      stat: 'Happy Teams'
    },
    {
      icon: CalendarCheck,
      title: 'One Platform',
      description: 'Payroll, invoicing, accounts, and HR together — no more switching between tools',
      stat: 'All-in-One'
    }
  ];

  // Maps each category's hex color to the matching CSS color-variant class.
  // Data (hex colors) is unchanged; this only selects which existing class to apply.
  const colorClassMap = {
    '#ec4899': 'color-accent-pink',
    '#8b5cf6': 'color-accent-purple',
    '#06b6d4': 'color-accent-cyan',
    '#10b981': 'color-accent-green'
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="features-page">
      {/* Hero Section */}
      <FeaturesHero />

      {/* Main Features Grid */}
      <section className="features-section">
        <div className="container">
          <div className="section-header">
            <h2>Core Features</h2>
          </div>
          <motion.div
            className="main-features-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {mainFeatures.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={idx}
                  className="main-feature-card"
                  variants={itemVariants}
                  whileHover={{ y: -8 }}
                >
                  <Icon size={36} className="main-feature-icon" />
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Feature Categories */}
      <section className="features-section features-section-alt">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>Feature Categories</h2>
            <p>Explore our comprehensive feature set organized by category</p>
          </motion.div>

          <motion.div
            className="category-cards-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {featureCategories.map((category, idx) => {
              const Icon = category.icon;
              const colorClass = colorClassMap[category.color] || '';
              return (
                <motion.div
                  key={idx}
                  className={`category-card ${colorClass}`}
                  variants={itemVariants}
                  whileHover={{ y: -8 }}
                >
                  <div className="card-header">
                    <div className={`category-icon-box ${colorClass}`}>
                      <Icon size={26} />
                    </div>
                    <h3>{category.title}</h3>
                  </div>

                  <div className="card-divider" />

                  <ul className="category-features-list">
                    {category.features.map((feature, fidx) => (
                      <li key={fidx}>
                        <CheckCircle size={17} className="feature-check" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="features-section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>Why Choose Effective Global Staff?</h2>
            <p>Discover the key benefits that make us the best choice for running your whole business</p>
          </motion.div>

          <motion.div
            className="benefits-cards-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {benefits.map((benefit, idx) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={idx}
                  className="benefit-card-item"
                  variants={itemVariants}
                  whileHover={{ y: -8 }}
                >
                  <div className="benefit-top">
                    <div className="benefit-icon-box">
                      <Icon size={24} />
                    </div>
                    <span className="benefit-stat-badge">{benefit.stat}</span>
                  </div>

                  <h3>{benefit.title}</h3>
                  <p>{benefit.description}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="features-cta">
        <div className="features-cta-bg" />
        <div className="container">
          <motion.div
            className="features-cta-content"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>Ready to Transform Your Whole Business?</h2>
            <p>Start your 14-day free trial and experience all these features</p>
            <a href="/signup" className="btn btn-secondary">
              Request Demo <ArrowRight size={16} />
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Features;